/**
 * editor2/hooks/usePaymentEmail.ts
 * PaymentEmailModal の状態管理フック
 * ReservationEditorPanel の paymentEmailModal 関連ロジックを抽出
 */
import { useState, useCallback } from 'react';
import { MAIL_FOOTER_TEMPLATES } from '../constants';
import type { EditorReservation } from '../types';
import type { PaymentEmailState } from '../modals/PaymentEmailModal';

const INITIAL_STATE: PaymentEmailState = {
  open: false,
  step: 'lang',
  type: 'payment',
  to: '',
  subject: '',
  body: '',
  sending: false,
  error: '',
};

interface UsePaymentEmailOptions {
  getData: () => EditorReservation;
  sysApiBase: string;
}

function getMailHeader(data: EditorReservation, lang: string): string {
  const name = data.customer_name || 'お客様';
  const code = data.contact_code || data.contact_id || data.id || '';

  let greeting = `${name} 様`;
  if (lang === 'en') greeting = `Dear ${name}`;
  if (lang === 'tw') greeting = `${name} 您好`;
  if (lang === 'kr') greeting = `${name} 고객님`;

  let label = '予約コード';
  if (lang === 'en') label = 'Reservation Code';
  if (lang === 'tw') label = '預約編號';
  if (lang === 'kr') label = '예약 코드';

  return `${greeting}\n${label}: ${code}\n\n`;
}

function getMailFooter(lang: string): string {
  return MAIL_FOOTER_TEMPLATES[lang] || MAIL_FOOTER_TEMPLATES['ja'];
}

function formatDate(dateStr: string, lang: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    if (lang === 'en') return `${y}/${m}/${day} ${h}:${min}`;
    if (lang === 'tw') return `${y}年${m}月${day}日 ${h}:${min}`;
    if (lang === 'kr') return `${y}년 ${m}월 ${day}일 ${h}:${min}`;
    return `${y}年${m}月${day}日 ${h}:${min}`;
  } catch { return dateStr; }
}

function formatPrice(price: number | null | undefined, lang: string): string {
  if (price == null) return '';
  const n = Number(price);
  if (isNaN(n)) return String(price);
  if (lang === 'en') return `¥${n.toLocaleString()}`;
  if (lang === 'tw') return `¥${n.toLocaleString()}`;
  if (lang === 'kr') return `¥${n.toLocaleString()}`;
  return `¥${n.toLocaleString()}`;
}

// --- リクエスト系テンプレート ---
function buildRequestOk(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nWe are pleased to inform you that the vehicle is available for your requested dates.\nPlease reply by 12:00 noon tomorrow to confirm your reservation.\n\nWe look forward to hearing from you.\n\n■Reservation Details\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n我們很高興通知您，您所希望的日期已確認有車輛可供使用。\n請在明天中午12點之前回覆，以正式確認您的預約。\n\n期待收到您的回覆。\n\n■預約內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 차량 확보가 가능하여 연락드립니다.\n내일 12시까지 회신해 주시면 정식으로 예약이 확정됩니다.\n\n바쁘시겠지만 연락 부탁드립니다.\n\n■예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\n\nご希望の日程にて在庫の確保ができましたので、ご連絡いたしました。\n明日の12時までにご返信をいただけますと、正式に予約確定となります。\n\nお忙しいところ恐縮ですが、ご連絡をお待ちしております。\n\n■予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

function buildRequestFull(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nUnfortunately, we were unable to secure a vehicle for your requested dates.\nWe sincerely apologize for the inconvenience.\nWe look forward to serving you in the future.\n\n■Requested Reservation\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n很遺憾，您所希望的日期無法確保車輛。\n非常抱歉造成不便。\n期待下次為您服務。\n\n■預約請求內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 차량 확보가 불가능했습니다.\n대단히 죄송합니다.\n다음 기회에 이용해 주시기를 기다리겠습니다.\n\n■리퀘스트 예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\nご希望の日程ですが在庫が確保できませんでした。\n大変申し訳ございません。\nまたのご予約をお待ちしております。\n\n■リクエスト予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

function buildCancel(_data: EditorReservation, lang: string): { subject: string; body: string } {
  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Cancellation Confirmation',
      body: `This is N RENT A CAR Naha Airport.\nWe have received your cancellation.\n\nWe are sorry to see you go and look forward to serving you again in the future.`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 取消確認',
      body: `N RENT A CAR 那霸機場店。\n我們已收到您的取消申請。\n\n非常遺憾，期待下次再為您服務。`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 취소 안내',
      body: `N 렌터카 나하 공항점입니다.\n취소가 접수되었습니다.\n\n대단히 아쉽지만, 다음 기회에 다시 이용해 주시기를 기다리겠습니다.`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。キャンセルを承りました。',
    body: `Nレンタカー 那覇空港店でございます。\nキャンセルを承りました。\n\n大変残念ではございますが、又のご利用をお待ちしております。`,
  };
}

function buildCancelFee(data: EditorReservation, lang: string): { subject: string; body: string } {
  const basePrice = formatPrice(data.base_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Cancellation and Cancellation Fee',
      body: `This is N RENT A CAR Naha Airport.\nWe have received your cancellation.\n\nWe regret to inform you that, in accordance with our cancellation policy, a cancellation fee will apply.\nBase price: ${basePrice}\nCancellation fee: [calculated amount]\n\nPlease let us know about this cancellation when making your next reservation.\nWe will offer you a discount.\n\nWe look forward to serving you again.`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 取消及取消費用通知',
      body: `N RENT A CAR 那霸機場店。\n我們已收到您的取消申請。\n\n非常抱歉，根據取消政策，將向您收取取消費用。\n基本費用: ${basePrice}\n取消費用: [計算金額]\n\n下次預約時請告知此次取消情況，我們將為您提供折扣。\n\n期待您再次光臨。`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 취소 및 취소 수수료 안내',
      body: `N 렌터카 나하 공항점입니다.\n취소가 접수되었습니다.\n\n대단히 죄송하지만, 취소 정책에 따라 취소 수수료가 부과됩니다.\n기본 요금: ${basePrice}\n취소 수수료: [계산된 금액]\n\n다음 예약 시 이번 취소 건에 대해 말씀해 주시면 할인을 적용해 드리겠습니다.\n\n다음 기회에 다시 이용해 주시기를 기다리겠습니다.`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。キャンセル料についてご連絡いたします。',
    body: `Nレンタカー 那覇空港店でございます。\nキャンセルを承りました。\n\n大変恐縮ではございますが、キャンセルポリシーに基づきキャンセル料をご請求いたします。\n基本料金: ${basePrice}\nキャンセル料金: [計算した料金]\n\n次回ご予約の際に、今回キャンセルとなった旨をお知らせください。\n割引をさせていただきます。\n\n又のご利用をお待ちしております。`,
  };
}

function buildRequestOffer(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nUnfortunately, we were unable to prepare the requested vehicle type for your dates.\nWe would like to offer an alternative vehicle. Please consider the following:\n\n■Alternative Offer\nVehicle: \nPickup: ${start}\nReturn: ${end}\nTotal: \n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n很遺憾，您所希望的日期無法準備所請求的車型。\n我們提供以下替代方案，請您考慮。\n\n■替代方案\n車型: \n取車日期: ${start}\n還車日期: ${end}\n總金額: \n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 희망 차종을 준비할 수 없었습니다.\n다른 차종으로 제안드립니다. 검토 부탁드립니다.\n\n■제안 내용\n차종: \n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: \n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\n\nご希望日時にご希望の車種をご用意することができませんでした。\n別の車種でのご提案でございます。ご検討をお願い致します。\n\n■ご提案内容\n車種: \n貸出日時: ${start}\n返却日時: ${end}\n合計金額: \n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

export default function usePaymentEmail({ getData, sysApiBase }: UsePaymentEmailOptions) {
  const [modalState, setModalState] = useState<PaymentEmailState>(INITIAL_STATE);

  const openPaymentEmail = useCallback(
    (type: string = 'payment') => {
      const data = getData();
      setModalState({
        open: true,
        step: 'lang',
        type,
        to: data.customer_email || '',
        subject: '',
        body: '',
        sending: false,
        error: '',
      });
    },
    [getData],
  );

  const handleLangSelect = useCallback(
    async (lang: string) => {
      const data = getData();
      const mode = modalState.type;
      const header = getMailHeader(data, lang);
      const footer = `\n\n${getMailFooter(lang)}`;
      let subject = '';
      let body = '';

      if (mode === 'free') {
        // Free-form email with just header + footer
        if (lang === 'en') {
          subject = '[N RENT A CAR] Information';
          body = `${header}This is N RENT A CAR.${footer}`;
        } else if (lang === 'tw') {
          subject = '[N RENT A CAR] 通知';
          body = `${header}N RENT A CAR 那霸機場店${footer}`;
        } else if (lang === 'kr') {
          subject = '[N RENT A CAR] 안내';
          body = `${header}N 렌터카 나하 공항점입니다.${footer}`;
        } else {
          subject = '[N RENT A CAR] ご案内';
          body = `${header}Nレンタカー 那覇空港店でございます。${footer}`;
        }

        setModalState((prev) => ({ ...prev, step: 'preview', subject, body }));
        return;
      }

      if (mode === 'comment') {
        const custComment = data.comment || '';
        if (lang === 'en') {
          subject = '[N RENT A CAR] Inquiry Response';
          body = `${header}This is N RENT A CAR Naha Airport.\nThank you for your inquiry.\n\n◼︎Comment\n${custComment}\n\n→[Reply to comment]\n\nPlease feel free to contact us if you have any questions.\nThank you.\n\nN RENT A CAR Naha Airport${footer}`;
        } else if (lang === 'tw') {
          subject = '[N RENT A CAR] 問題回覆';
          body = `${header}N RENT A CAR 那霸機場店\n感謝您的諮詢。\n\n◼︎留言\n${custComment}\n\n→[留言回覆]\n\n若有任何問題，歡迎隨時與我們聯繫。\n感謝您的配合。\n\nN RENT A CAR 那霸機場店${footer}`;
        } else if (lang === 'kr') {
          subject = '[N RENT A CAR] 문의 내용 안내';
          body = `${header}N 렌터카 나하 공항점입니다.\n문의해 주셔서 진심으로 감사드립니다.\n\n◼︎코멘트\n${custComment}\n\n→[코멘트 답변]\n\n궁금한 점이 있으시면 언제든지 편하게 문의해 주시기 바랍니다.\n감사합니다.\n\nN 렌터카 나하 공항점${footer}`;
        } else {
          subject = '[N RENT A CAR] お問い合わせ内容について';
          body = `${header}Nレンタカー 那覇空港店でございます。\nこの度は、お問い合わせをいただきまして、誠にありがとうございます。\n\n◼︎コメント\n${custComment}\n\n→[コメントへの返信]\n\nまた何かございましたらご気軽にいつでもお問い合わせください。\n何卒よろしくお願いいたします。\n\nNレンタカー 那覇空港店${footer}`;
        }

        setModalState((prev) => ({ ...prev, step: 'preview', subject, body }));
        return;
      }

      // リクエスト系・キャンセル系テンプレート（フロントで生成）
      if (
        mode === 'request_ok' ||
        mode === 'request_full' ||
        mode === 'request_offer' ||
        mode === 'cancel' ||
        mode === 'cancel_fee'
      ) {
        const builder = mode === 'request_ok' ? buildRequestOk
          : mode === 'request_full' ? buildRequestFull
          : mode === 'request_offer' ? buildRequestOffer
          : mode === 'cancel' ? buildCancel
          : buildCancelFee;
        const tpl = builder(data, lang);
        subject = tpl.subject;
        body = `${header}${tpl.body}${footer}`;

        setModalState((prev) => ({ ...prev, step: 'preview', subject, body }));
        return;
      }

      // payment: fetch from API
      try {
        const response = await fetch(`${sysApiBase}/get_payment_email_preview.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lang,
            customer_name: data.customer_name,
            contact_code: data.contact_code || data.contact_id || data.id,
            page_id: data.page_id || data.id,
            license: !!data.license,
            agreement: !!data.agreement,
          }),
        });
        const result = await response.json();

        subject = result.subject || '[N RENT A CAR] Payment Confirmation';
        if (lang === 'ja' && !subject.includes('支払い確認'))
          subject = '[N RENT A CAR] 支払い確認のお願い';

        body = `${header}${result.body}${footer}`;
      } catch (error) {
        console.error('Failed to fetch payment email preview', error);
        alert('メールテンプレートの取得に失敗しました');
        return;
      }

      setModalState((prev) => ({ ...prev, step: 'preview', subject, body }));
    },
    [getData, modalState.type, sysApiBase],
  );

  const handleSend = useCallback(() => {
    if (!modalState.to) {
      setModalState((prev) => ({ ...prev, error: '送信先メールアドレスがありません' }));
      return null;
    }

    const emailData = {
      to: modalState.to,
      subject: modalState.subject,
      body: modalState.body,
    };

    // Close immediately
    setModalState((prev) => ({ ...prev, open: false, sending: false }));
    return emailData;
  }, [modalState]);

  const closePaymentEmail = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    modalState,
    setModalState,
    openPaymentEmail,
    handleLangSelect,
    handleSend,
    closePaymentEmail,
  };
}
